import { PreferredContactMethodEnum } from '@inosio/payments-sdk';
import { Box, CircularProgress, FormControl, Grid, IconButton } from '@mui/material';
import CopyToClipboard from 'react-copy-to-clipboard';
import copyclipboard from '../../../icons/copyclipboard.svg';
import { useEffect, useState } from 'react';
import PhoneInput, { CountryData, PhoneInputProps } from 'react-phone-input-2';

export interface WorkOrderPhoneFieldProps {
  /**
   * The width of the field in Material UI grid units.
   * Takes a value ranging from 0 to 12, 12 being full width, 6 being half etc
   */
  width: number;

  /**
   * The value to be displayed and copied
   */
  value: string;

  /**
   * Label to be displayed
   */
  label: string;

  /**
   * Optional prop provided when the field is displaying contact methods to show if it's the
   * preferred contact method. Takes in the PreferredContactMethodEnum.
   */
  methodPreferred?: PreferredContactMethodEnum | null;

  /**
   * Callback that fires when the copy to clipboard icon is clicked in the field
   * @returns void
   */
  handleClickCopyToClipboard: () => void;

  /**
   * If true, allows the field to be edited
   */
  isEditable?: boolean;

  /**
   * If true, disables the field and shows a loading indicator
   */
  isUpdating?: boolean;

  /**
   * Callback to handle changes to the field value in edit mode
   */
  onValueChange?: (newValue: string) => void;

  /**
   * Callback that fires when the field loses focus and the value has changed.
   * @param newValue The updated value
   */
  onBlurUpdate?: (newValue: string) => void;


   /**
   * Properties to customize the FormControl component.
   * Contains optional className for styling.
   */
   formControlProps?: {
    className: string | undefined;
  };

  /**
   * Properties to customize the icon.
   * Contains optional className for styling.
   */
  iconProps?: {
    className: string | undefined;
  };

  /**
   * Properties to customize the PhoneInput component.
   */
  inputProps?: PhoneInputProps;
}

export const WorkOrderPhoneField = (props: WorkOrderPhoneFieldProps) => {
  const [editableValue, setEditableValue] = useState<string>(props.value);

  useEffect(() => {
    setEditableValue(props.value)
  }, [props.value]);

  const handleChange = (value: string, data: CountryData | {}, event: React.ChangeEvent<HTMLInputElement>, formattedValue: string) => {
    setEditableValue(value);
    if (props.onValueChange) {
      props.onValueChange(value);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>, data: CountryData | {}) => {
    const previouseContactPhone = props.value.replace("+", "").trim();
    const newContactPhone = editableValue.trim()
    if (newContactPhone !== previouseContactPhone && props.onBlurUpdate) {
      props.onBlurUpdate(editableValue.trim());
    }
  };

  return (
    <Grid item xs={props.width} mt={1} mb={1}>
      <FormControl fullWidth className={props.formControlProps?.className}>
      <Box display="flex" alignItems="center">
          <Box className={props.iconProps?.className}>
            <CopyToClipboard text={editableValue}>
              <IconButton onClick={props.handleClickCopyToClipboard} disabled={props.isUpdating}>
                {props.isUpdating ? (
                  <CircularProgress size={20} />
                ) : (
                  <img src={copyclipboard} style={{ height: '20px', width: '20px' }} alt="copy" />
                )}
              </IconButton>
            </CopyToClipboard>
          </Box>
          </Box>
        <PhoneInput
          inputStyle={{
            cursor: 'pointer',
          }}
          specialLabel={props.label}
          value={editableValue}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={!props.isEditable || props.isUpdating} 
          {...props.inputProps}
        />
      </FormControl>
    </Grid>
  );
};